/**
 * [PURPOSE]: Challenge Rating (CR) calculator — DMG p.273-274 formula.
 *   Computes defensive CR (from HP + AC) and offensive CR (from DPR +
 *   attack bonus / save DC), averages them for final CR. Adjusts for
 *   notable defensive/offensive features including the DMG p.280 special-
 *   ability step (damage resistances/immunities raise effective HP;
 *   save-or-die / full-incapacitation effects raise effective DPR).
 * [WHY]: Spec D Task 3 — Monster Editor surfaces CR alongside CPI for
 *   back-compat. Players familiar with D&D CR get a number they
 *   recognize; the CPI/SPI drawer gives the deeper view.
 * [SCOPE]: This is the canonical DMG formula. NOT the BibleBuilder
 *   Challenge Index — that's a separate (encounter-difficulty) metric
 *   in cpiEngine.js.
 * [RULE 22]: Pure function. All inputs from structured monster
 *   datablocks (no flavor-text parsing).
 * [SPEC]: docs/superpowers/specs/2026-05-17-combat-manager-design.md
 * [PLAN]: docs/superpowers/plans/2026-05-17-combat-manager.md Task 3
 *
 * DMG p.274 Monster Statistics by Challenge Rating table (abridged):
 *
 *   CR | XP    | Prof | AC | HP        | Attack | DPR     | Save DC
 *   ---|-------|------|----|-----------|--------|---------|--------
 *   0  | 0/10  | +2   | 13 | 1-6       | +3     | 0-1     | 13
 *   1/8| 25    | +2   | 13 | 7-35      | +3     | 2-3     | 13
 *   1/4| 50    | +2   | 13 | 36-49     | +3     | 4-5     | 13
 *   1/2| 100   | +2   | 13 | 50-70     | +3     | 6-8     | 13
 *   1  | 200   | +2   | 13 | 71-85     | +3     | 9-14    | 13
 *   2  | 450   | +2   | 13 | 86-100    | +3     | 15-20   | 13
 *   3  | 700   | +2   | 13 | 101-115   | +4     | 21-26   | 13
 *   4  | 1100  | +2   | 14 | 116-130   | +5     | 27-32   | 14
 *   5  | 1800  | +3   | 15 | 131-145   | +6     | 33-38   | 15
 *   6  | 2300  | +3   | 15 | 146-160   | +6     | 39-44   | 15
 *   7  | 2900  | +3   | 15 | 161-175   | +6     | 45-50   | 15
 *   8  | 3900  | +3   | 16 | 176-190   | +7     | 51-56   | 16
 *   9  | 5000  | +4   | 16 | 191-205   | +7     | 57-62   | 16
 *   10 | 5900  | +4   | 17 | 206-220   | +7     | 63-68   | 16
 *   ... (extends to CR 30 — see DMG p.274)
 *
 * DMG p.280 special-ability adjustment (implemented in computeCR):
 *
 *   Effective HP multiplier (based on damage immunities/resistances to
 *   common damage types — bludgeoning/piercing/slashing/fire/cold/
 *   lightning/thunder/necrotic/poison/radiant/acid):
 *     3+ immunities              → ×2.00
 *     1–2 immunities + 2+ res.  → ×1.75
 *     1–2 immunities OR 3+ res. → ×1.50
 *     1–2 resistances only      → ×1.25
 *     none                      → ×1.00
 *
 *   Offensive CR bump (for save-or-die / full-incapacitation effects):
 *     causeCondition of unconscious / paralyzed / stunned / incapacitated
 *     → +2 CR index steps added to offensive before averaging.
 *     (Soft conditions like prone/grappled/restrained/frightened are
 *     captured by their DPR contribution and do NOT trigger this bump.)
 */

// CR table — each row defines the boundary for that CR.
// HP / AC / DPR / attack / save DC anchors per DMG p.274.
const CR_TABLE = [
  { cr: '0',   xp: 10,      prof: 2, ac: 13, hpMax: 6,   attack: 3, dprMax: 1,   saveDc: 13 },
  { cr: '1/8', xp: 25,      prof: 2, ac: 13, hpMax: 35,  attack: 3, dprMax: 3,   saveDc: 13 },
  { cr: '1/4', xp: 50,      prof: 2, ac: 13, hpMax: 49,  attack: 3, dprMax: 5,   saveDc: 13 },
  { cr: '1/2', xp: 100,     prof: 2, ac: 13, hpMax: 70,  attack: 3, dprMax: 8,   saveDc: 13 },
  { cr: '1',   xp: 200,     prof: 2, ac: 13, hpMax: 85,  attack: 3, dprMax: 14,  saveDc: 13 },
  { cr: '2',   xp: 450,     prof: 2, ac: 13, hpMax: 100, attack: 3, dprMax: 20,  saveDc: 13 },
  { cr: '3',   xp: 700,     prof: 2, ac: 13, hpMax: 115, attack: 4, dprMax: 26,  saveDc: 13 },
  { cr: '4',   xp: 1100,    prof: 2, ac: 14, hpMax: 130, attack: 5, dprMax: 32,  saveDc: 14 },
  { cr: '5',   xp: 1800,    prof: 3, ac: 15, hpMax: 145, attack: 6, dprMax: 38,  saveDc: 15 },
  { cr: '6',   xp: 2300,    prof: 3, ac: 15, hpMax: 160, attack: 6, dprMax: 44,  saveDc: 15 },
  { cr: '7',   xp: 2900,    prof: 3, ac: 15, hpMax: 175, attack: 6, dprMax: 50,  saveDc: 15 },
  { cr: '8',   xp: 3900,    prof: 3, ac: 16, hpMax: 190, attack: 7, dprMax: 56,  saveDc: 16 },
  { cr: '9',   xp: 5000,    prof: 4, ac: 16, hpMax: 205, attack: 7, dprMax: 62,  saveDc: 16 },
  { cr: '10',  xp: 5900,    prof: 4, ac: 17, hpMax: 220, attack: 7, dprMax: 68,  saveDc: 16 },
  { cr: '11',  xp: 7200,    prof: 4, ac: 17, hpMax: 235, attack: 8, dprMax: 74,  saveDc: 17 },
  { cr: '12',  xp: 8400,    prof: 4, ac: 17, hpMax: 250, attack: 8, dprMax: 80,  saveDc: 17 },
  { cr: '13',  xp: 10000,   prof: 5, ac: 18, hpMax: 265, attack: 8, dprMax: 86,  saveDc: 18 },
  { cr: '14',  xp: 11500,   prof: 5, ac: 18, hpMax: 280, attack: 8, dprMax: 92,  saveDc: 18 },
  { cr: '15',  xp: 13000,   prof: 5, ac: 18, hpMax: 295, attack: 8, dprMax: 98,  saveDc: 18 },
  { cr: '16',  xp: 15000,   prof: 5, ac: 18, hpMax: 310, attack: 9, dprMax: 104, saveDc: 18 },
  { cr: '17',  xp: 18000,   prof: 6, ac: 19, hpMax: 325, attack: 10, dprMax: 110, saveDc: 19 },
  { cr: '18',  xp: 20000,   prof: 6, ac: 19, hpMax: 340, attack: 10, dprMax: 116, saveDc: 19 },
  { cr: '19',  xp: 22000,   prof: 6, ac: 19, hpMax: 355, attack: 10, dprMax: 122, saveDc: 19 },
  { cr: '20',  xp: 25000,   prof: 6, ac: 19, hpMax: 400, attack: 10, dprMax: 140, saveDc: 19 },
  { cr: '21',  xp: 33000,   prof: 7, ac: 19, hpMax: 445, attack: 11, dprMax: 158, saveDc: 20 },
  { cr: '22',  xp: 41000,   prof: 7, ac: 19, hpMax: 490, attack: 11, dprMax: 176, saveDc: 20 },
  { cr: '23',  xp: 50000,   prof: 7, ac: 19, hpMax: 535, attack: 11, dprMax: 194, saveDc: 20 },
  { cr: '24',  xp: 62000,   prof: 7, ac: 19, hpMax: 580, attack: 11, dprMax: 212, saveDc: 21 },
  { cr: '25',  xp: 75000,   prof: 8, ac: 19, hpMax: 625, attack: 12, dprMax: 230, saveDc: 21 },
  { cr: '26',  xp: 90000,   prof: 8, ac: 19, hpMax: 670, attack: 12, dprMax: 248, saveDc: 21 },
  { cr: '27',  xp: 105000,  prof: 8, ac: 19, hpMax: 715, attack: 13, dprMax: 266, saveDc: 22 },
  { cr: '28',  xp: 120000,  prof: 8, ac: 19, hpMax: 760, attack: 13, dprMax: 284, saveDc: 22 },
  { cr: '29',  xp: 135000,  prof: 9, ac: 19, hpMax: 805, attack: 13, dprMax: 302, saveDc: 22 },
  { cr: '30',  xp: 155000,  prof: 9, ac: 19, hpMax: 850, attack: 14, dprMax: 320, saveDc: 23 },
];

// ---------------------------------------------------------------------------
// DMG p.280 special-ability adjustment constants
// ---------------------------------------------------------------------------

/**
 * Damage types that MOST player characters deal (and therefore matter for
 * the "how much does immunity/resistance help?" multiplier from DMG p.280).
 * Excludes force/thunder/radiant which are less commonly available to PCs.
 *
 * @internal
 */
const COMMON_DAMAGE_TYPES = new Set([
  'bludgeoning', 'piercing', 'slashing',
  'fire', 'cold', 'lightning', 'thunder',
  'necrotic', 'poison', 'radiant', 'acid',
]);

/**
 * Conditions that effectively end a combat encounter for the affected
 * creature (save-or-die / full-incapacitation tier). These warrant an
 * offensive CR bump per DMG p.280.
 *
 * Soft conditions (prone, grappled, restrained, frightened) are already
 * partly captured by their DPR contributions and do NOT trigger the bump.
 *
 * @internal
 */
const STRONG_DEBILITATE_CONDITIONS = new Set([
  'unconscious', 'paralyzed', 'stunned', 'incapacitated',
]);

// ---------------------------------------------------------------------------
// DMG p.280 helper functions (exported for direct test assertions — Rule 22)
// ---------------------------------------------------------------------------

/**
 * Compute the effective HP multiplier from damage immunities + resistances
 * per DMG p.280. A monster immune/resistant to types that most PCs deal
 * is harder to kill than its raw HP suggests.
 *
 * Tiers (DMG p.280, codified heuristic):
 *   3+ immunities to common types                → ×2.00
 *   1–2 immunities + 2+ resistances              → ×1.75
 *   1–2 immunities OR 3+ resistances (no immunity) → ×1.50
 *   1–2 resistances only                         → ×1.25
 *   none                                         → ×1.00
 *
 * [RULE 22]: Pure function — no flavor-text parsing; property arrays only.
 *
 * @param {Object} monster
 * @param {string[]} [monster.damageImmunities]
 * @param {string[]} [monster.damageResistances]
 * @returns {number} multiplier ≥ 1.0
 */
export function computeEffectiveHPMultiplier(monster) {
  if (!monster) return 1.0;
  const immunities = (monster.damageImmunities || []).filter(
    (t) => COMMON_DAMAGE_TYPES.has(t)
  ).length;
  const resistances = (monster.damageResistances || []).filter(
    (t) => COMMON_DAMAGE_TYPES.has(t)
  ).length;
  if (immunities >= 3) return 2.0;
  if (immunities >= 1 && resistances >= 2) return 1.75;
  if (immunities >= 1 || resistances >= 3) return 1.5;
  if (resistances >= 1) return 1.25;
  return 1.0;
}

/**
 * Compute the number of additional CR index steps to add to offensive CR
 * when the monster has save-or-die / full-incapacitation effects (DMG p.280).
 *
 * Returns +2 if any action or trait has a causeCondition effect with a
 * STRONG_DEBILITATE_CONDITIONS value (unconscious / paralyzed / stunned /
 * incapacitated). Returns 0 otherwise.
 *
 * [RULE 22]: Pure function — driven by structured effects arrays, not
 *   flavor-text parsing or name strings.
 *
 * @param {Object} monster
 * @param {Array} [monster.actions]
 * @param {Array} [monster.traits]
 * @returns {number} 0 or 2 CR-index steps
 */
export function computeOffensiveCRBump(monster) {
  if (!monster) return 0;
  const allAbilities = [
    ...(monster.actions || []),
    ...(monster.traits || []),
  ];
  const hasStrongDebilitate = allAbilities.some((ability) =>
    (ability.effects || []).some(
      (e) =>
        e.type === 'causeCondition' &&
        STRONG_DEBILITATE_CONDITIONS.has(e.value)
    )
  );
  return hasStrongDebilitate ? 2 : 0;
}

// ---------------------------------------------------------------------------

/**
 * Parse a dice formula like '2d6+3' or '1d10' into average damage.
 * Returns 0 for empty/null/malformed input.
 *
 * @param {string} formula
 * @returns {number}
 */
export function parseDiceAverage(formula) {
  if (!formula || typeof formula !== 'string') return 0;
  let total = 0;
  // Match all NdM pieces
  for (const match of formula.matchAll(/(\d+)d(\d+)/g)) {
    const num = parseInt(match[1], 10);
    const die = parseInt(match[2], 10);
    if (Number.isFinite(num) && Number.isFinite(die) && num > 0 && die > 0) {
      total += num * (1 + die) / 2;
    }
  }
  // Flat bonus at the end: trailing "+N" or " +N"
  const bonusMatch = formula.match(/[+-]\s*(\d+)\s*$/);
  if (bonusMatch) {
    const sign = bonusMatch[0].trim().startsWith('-') ? -1 : 1;
    total += sign * parseInt(bonusMatch[1], 10);
  }
  return total;
}

/**
 * Compute defensive CR from HP + AC. Returns the CR row whose hpMax
 * bracket contains the monster's HP, then adjusts by AC delta vs the
 * expected AC for that CR: +1 CR per 2 points AC above expected;
 * -1 CR per 2 points below.
 *
 * @param {{ hp: { value: number }, ac: { value: number } }} monster
 * @returns {{ cr: string, index: number }}
 */
export function defensiveCR(monster) {
  if (!monster || !monster.hp || !monster.ac) {
    return { cr: '0', index: 0 };
  }
  const hp = Number(monster.hp.value) || 0;
  const ac = Number(monster.ac.value) || 0;

  // Find CR by HP bracket.
  let baseIdx = 0;
  for (let i = 0; i < CR_TABLE.length; i++) {
    if (hp <= CR_TABLE[i].hpMax) { baseIdx = i; break; }
    baseIdx = CR_TABLE.length - 1; // saturate at CR 30
  }

  // AC adjustment.
  const expectedAC = CR_TABLE[baseIdx].ac;
  const acDelta = Math.floor((ac - expectedAC) / 2);
  const adjustedIdx = Math.max(0, Math.min(CR_TABLE.length - 1, baseIdx + acDelta));

  return { cr: CR_TABLE[adjustedIdx].cr, index: adjustedIdx };
}

/**
 * Compute average DPR over 3 rounds from a monster's actions[]. Counts
 * the highest-damage standard action as round-1 DPR. Multiattack
 * actions multiply by their attack count when described.
 *
 * @param {{ actions: Array }} monster
 * @returns {number}
 */
export function computeAverageDPR(monster) {
  if (!monster || !Array.isArray(monster.actions)) return 0;

  // Find multiattack pattern: actions array with kind:'multiattack'
  const multi = monster.actions.find((a) => a.kind === 'multiattack');

  // Collect per-action DPR.
  const perAction = [];
  for (const action of monster.actions) {
    if (action.kind === 'multiattack') continue;
    if (!action.damage) continue;
    const avg = parseDiceAverage(action.damage);
    perAction.push({ name: action.name, avg });
  }
  perAction.sort((a, b) => b.avg - a.avg);

  if (perAction.length === 0) return 0;

  // Simple heuristic: with multiattack, DPR = top action's avg × (multiattack count, default 2).
  // Without multiattack, DPR = top action's avg.
  const topAvg = perAction[0].avg;
  if (multi) {
    // [NS-C12]: structured count wins; text-parse is the legacy fallback.
    if (Number.isFinite(multi.count) && multi.count > 0) {
      return topAvg * multi.count;
    }
    // Try to parse the multiattack count from the description (e.g., "makes two attacks", "three attacks").
    const text = String(multi.description || '');
    const numWord = { one: 1, two: 2, three: 3, four: 4, five: 5 };
    let count = 2;
    for (const [word, n] of Object.entries(numWord)) {
      if (new RegExp(`\\b${word}\\b`, 'i').test(text)) { count = n; break; }
    }
    return topAvg * count;
  }
  return topAvg;
}

/**
 * Compute offensive CR from average DPR + attack bonus. Returns the
 * CR row whose dprMax bracket contains the monster's DPR, adjusted by
 * attack-bonus delta vs the expected attack bonus for that CR
 * (+1 CR per 2 points attack above expected; -1 CR per 2 below).
 *
 * @param {Object} monster
 * @returns {{ cr: string, index: number, dpr: number }}
 */
export function offensiveCR(monster) {
  const dpr = computeAverageDPR(monster);

  let baseIdx = 0;
  for (let i = 0; i < CR_TABLE.length; i++) {
    if (dpr <= CR_TABLE[i].dprMax) { baseIdx = i; break; }
    baseIdx = CR_TABLE.length - 1;
  }

  // Attack-bonus delta: pull the highest attack bonus from the actions[].
  let attackBonus = 0;
  for (const action of monster.actions || []) {
    if (typeof action.attackBonus === 'number' && action.attackBonus > attackBonus) {
      attackBonus = action.attackBonus;
    }
  }
  const expectedAttack = CR_TABLE[baseIdx].attack;
  const attackDelta = Math.floor((attackBonus - expectedAttack) / 2);
  const adjustedIdx = Math.max(0, Math.min(CR_TABLE.length - 1, baseIdx + attackDelta));

  return { cr: CR_TABLE[adjustedIdx].cr, index: adjustedIdx, dpr };
}

/**
 * Compute final CR as the average of defensive and offensive CR indices,
 * with DMG p.280 special-ability adjustments applied:
 *   - Defensive CR is computed using effective HP (raw HP × immunity/resistance
 *     multiplier from computeEffectiveHPMultiplier).
 *   - Offensive CR index is bumped by computeOffensiveCRBump for monsters with
 *     save-or-die / full-incapacitation effects.
 *
 * The raw `defensive` and `offensive` fields in the return value reflect the
 * ADJUSTED CRs used to compute the average, so MonsterEditor hint surfaces
 * ("your defensive CR is X") are self-consistent with the final average.
 *
 * @param {Object} monster
 * @returns {{ defensive: string, offensive: string, average: string, xp: number }}
 */
export function computeCR(monster) {
  // DMG p.280: adjust effective HP for resistances/immunities.
  const hpMultiplier = computeEffectiveHPMultiplier(monster);
  const rawHP = (monster && monster.hp) ? Number(monster.hp.value) || 0 : 0;
  const effectiveHP = Math.round(rawHP * hpMultiplier);

  // Build a lightweight proxy for defensiveCR that uses effective HP but
  // preserves the real AC (AC is not affected by damage resistances).
  const defMonster = hpMultiplier !== 1.0
    ? { hp: { value: effectiveHP }, ac: monster.ac }
    : monster;

  const def = defensiveCR(defMonster);
  const off = offensiveCR(monster);

  // DMG p.280: bump offensive for save-or-die / full-incapacitation effects.
  const offBump = computeOffensiveCRBump(monster);
  const avgIdx = Math.round((def.index + off.index + offBump) / 2);
  const clamped = Math.max(0, Math.min(CR_TABLE.length - 1, avgIdx));

  // Recompute the adjusted offensive string for consumer display.
  const adjOffIdx = Math.min(CR_TABLE.length - 1, off.index + offBump);

  return {
    defensive: def.cr,
    offensive: CR_TABLE[adjOffIdx].cr,
    average: CR_TABLE[clamped].cr,
    xp: CR_TABLE[clamped].xp,
  };
}

/**
 * Expose the full CR table for consumers that need the anchors (e.g.,
 * the Monster Editor's hint surface "for CR 5 you'd expect ~145 HP /
 * AC 15 / +6 attack / 33-38 DPR").
 */
export const CR_ANCHORS = Object.freeze(CR_TABLE);

/**
 * Lookup XP value for a given CR string. DMG p.275 table.
 * CR can be '0', '1/8', '1/4', '1/2', or any integer 1-30 (string or number).
 * Falls back to 0 for unknown CR.
 *
 * @param {string|number} cr
 * @returns {number} XP value (0 if not found)
 */
export function xpForCR(cr) {
  if (cr === null || cr === undefined) return 0;
  const key = String(cr);
  const row = CR_TABLE.find((r) => r.cr === key);
  return row ? row.xp : 0;
}

/**
 * Lookup the expected Proficiency Bonus for a given CR (DMG p.274).
 * Used by MonsterEditor to auto-suggest PB matching the author's chosen CR
 * (Stone 5 polish / #102 — "formula suggests, user overrides" UX contract).
 *
 * @param {string|number} cr
 * @returns {number|null} expected PB (+2..+9), or null when CR is unknown
 */
export function expectedPBForCR(cr) {
  if (cr === null || cr === undefined) return null;
  const key = String(cr);
  const row = CR_TABLE.find((r) => r.cr === key);
  return row ? row.prof : null;
}
